<template>
  <DModal :show-close="false" :isBgOverlay="true" @close="$emit('close')" :appendToBody="true"
          style="z-index: 12312312312313" :placeInTop="true">
    <div class="d-modal" slot="insertComponent">
      <div class="d-modal__header">
        <div>
          <h2>Beregn UV-tillæg for tidsbegrænset periode</h2>
          <p class="tuv__subtitle">{{ userName }}</p>
        </div>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="d-modal__content">
        <p class="tuv__intro">
          Ansættelsen har en afvigende periode. Beregn UV-tillægget ud fra skoledage og UV-tid, så det matcher
          den faktiske periode. Tillægget lægges som kladde i listen og sendes ikke til lønsystemet.
        </p>

        <div class="tuv__facts">
          <div class="tuv__fact">
            <span class="tuv__fact-label">Ansættelsesgrad</span>
            <span class="tuv__fact-value">{{ ansaettelsesgradPercent != null ? formatPercent(ansaettelsesgradPercent) : '—' }}</span>
          </div>
          <div class="tuv__fact">
            <span class="tuv__fact-label">Periode</span>
            <span class="tuv__fact-value">{{ periodLabel }}</span>
          </div>
          <div class="tuv__fact">
            <span class="tuv__fact-label">Bruttotid</span>
            <span class="tuv__fact-value">{{ yearNormHours != null ? formatDecimal(yearNormHours) : '—' }}</span>
          </div>
          <div class="tuv__fact">
            <span class="tuv__fact-label">Lønkode</span>
            <span class="tuv__fact-value">{{ salaryCodeLabel }}</span>
          </div>
        </div>

        <div class="do-form-row">
          <div class="d-input-group">
            <label class="d-label">Skoledage i skoleåret</label>
            <input type="number" min="0" inputmode="numeric" class="d-input" v-model="schoolDaysInYear" placeholder="fx 200">
          </div>
          <div class="d-input-group">
            <label class="d-label">Skoledage i periode</label>
            <input type="number" min="0" inputmode="numeric" class="d-input" v-model="schoolDaysInPeriod" placeholder="fx 46">
          </div>
          <div class="d-input-group">
            <label class="d-label">UV-grænse</label>
            <input type="text" inputmode="decimal" class="d-input" v-model="graense" placeholder="750">
          </div>
        </div>

        <div class="d-input-group">
          <label class="d-label">Justeret timetal</label>
          <input type="text" inputmode="decimal" class="d-input" v-model="adjustedHours" placeholder="Total UV-tid for perioden">
          <p class="tuv__hint">
            Som standard tæller al tildelt UV-tid med i beregningen. Hvis kun de skemalagte timer i perioden skal
            bruges, rettes timetallet her. Husk også at opdatere opgaveoversigten, da det er den der danner grundlag
            for løntilsagnet.
          </p>
        </div>

        <div class="tuv__result" v-if="isCalcValid">
          <div class="tuv__result-steps">
            <div class="tuv__result-row">
              <span>UV-timer omregnet til fuld tid</span>
              <span>{{ formatDecimal(uvOmregnetFuldtid) }}</span>
            </div>
            <div class="tuv__result-row">
              <span>Overskud over grænse (fuld tid)</span>
              <span>{{ formatDecimal(surplusFuldtid) }}</span>
            </div>
          </div>
          <div class="tuv__result-total">
            <span>UV-tillæg der udbetales i perioden</span>
            <strong>{{ formatDecimal(resultUnits) }} timer</strong>
          </div>
          <div class="tuv__result-row tuv__result-row--sub" v-if="resultUnits > 0">
            <span>Fordeles på</span>
            <span>{{ formatDecimal(unitsPerInstallment) }} enh./md · {{ installments }} {{ installments === 1 ? 'måned' : 'måneder' }}</span>
          </div>
          <p class="tuv__result-note" v-if="resultUnits <= 0">
            UV-tiden er under grænsen i perioden – der dannes ikke noget tillæg.
          </p>
        </div>

        <p class="tuv__result-note" v-if="!salaryCodeId">
          Stillingskategorien mangler en "Lønkode over UV-grænse", så der kan ikke dannes et tillæg.
        </p>
      </div>

      <div class="d-modal__buttons">
        <div class="d-modal__buttons__left"></div>
        <div class="d-modal__buttons__right">
          <button class="do-bttn" @click="$emit('close')">{{ $t('vue2.common.cancel') }}</button>
          <button class="do-bttn do-bttn--submit"
                  :class="{'do-bttn--submit--loading': isSaving}"
                  :disabled="!isValid || isSaving"
                  @click="generate">
            Dan UV-tillæg i listen
          </button>
        </div>
      </div>
    </div>
  </DModal>
</template>

<script>
import DModal from '../common/DModal.vue'
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import moment from 'moment'

export default {
  name: 'UvLimitTidsbegraensetModal',
  components: {DModal},
  props: {
    schoolId: {
      default: () => null
    },
    schoolYearId: {
      default: () => null
    },
    userId: {
      default: () => null
    },
    userName: {
      type: String,
      default: ''
    },
    employmentId: {
      default: () => null
    },
    ansaettelsesgradPercent: {
      default: () => null
    },
    yearNormHours: {
      default: () => null
    },
    periodStart: {
      default: () => null
    },
    periodEnd: {
      default: () => null
    },
    assignedTeachingHours: {
      default: () => null
    },
    initialGraense: {
      default: () => null
    },
    salaryCodeId: {
      default: () => null
    }
  },
  data () {
    return {
      schoolDaysInYear: '',
      schoolDaysInPeriod: '',
      graense: this.initialGraense != null && this.initialGraense !== '' ? String(parseFloat(this.initialGraense)) : '750',
      adjustedHours: this.assignedTeachingHours != null ? String(this.assignedTeachingHours) : '',
      isSaving: false
    }
  },
  computed: {
    salaryCodes () {
      return this.$store.getters.salaryCodesSorted || []
    },
    salaryCode () {
      if (this.salaryCodeId == null) return null
      return this.salaryCodes.find(code => String(code.id) === String(this.salaryCodeId)) || null
    },
    salaryCodeLabel () {
      if (!this.salaryCode) return '—'
      return this.salaryCode.vSupplementTitle || this.salaryCode.code || '—'
    },
    periodLabel () {
      if (!this.periodStart || !this.periodEnd) return '—'
      return moment(this.periodStart).format('DD-MM-YYYY') + ' til ' + moment(this.periodEnd).format('DD-MM-YYYY')
    },
    g () {
      return this.ansaettelsesgradPercent != null ? parseFloat(this.ansaettelsesgradPercent) / 100 : null
    },
    uvOmregnetFuldtid () {
      const t = this.parseNumber(this.adjustedHours)
      const n = this.parseNumber(this.schoolDaysInPeriod)
      const big = this.parseNumber(this.schoolDaysInYear)
      if (t == null || n == null || big == null || !this.g || n === 0) return null
      return (t / this.g) * (big / n)
    },
    surplusFuldtid () {
      if (this.uvOmregnetFuldtid == null) return null
      const limit = this.parseNumber(this.graense)
      return this.uvOmregnetFuldtid - (limit != null ? limit : 0)
    },
    // Slutresultat omregnet tilbage til perioden: overskud × ansættelsesgrad × (skoledage i periode / skoledage i året).
    resultUnits () {
      if (this.surplusFuldtid == null) return null
      const n = this.parseNumber(this.schoolDaysInPeriod)
      const big = this.parseNumber(this.schoolDaysInYear)
      if (n == null || big == null || big === 0) return null
      const units = this.surplusFuldtid * this.g * (n / big)
      return units > 0 ? units : 0
    },
    installments () {
      if (!this.periodStart || !this.periodEnd) return 1
      const start = moment(this.periodStart)
      const end = moment(this.periodEnd)
      const months = (end.month() - start.month()) + (end.year() - start.year()) * 12 + 1
      return months > 0 ? months : 1
    },
    unitsPerInstallment () {
      if (this.resultUnits == null) return null
      const inst = this.installments
      return inst > 0 ? this.resultUnits / inst : this.resultUnits
    },
    isCalcValid () {
      const daysYear = this.parseNumber(this.schoolDaysInYear)
      const daysPeriod = this.parseNumber(this.schoolDaysInPeriod)
      const hours = this.parseNumber(this.adjustedHours)
      return daysYear != null && daysYear > 0 &&
        daysPeriod != null && daysPeriod > 0 &&
        hours != null && hours >= 0 &&
        this.g != null && this.g > 0
    },
    isValid () {
      return !!this.userId && !!this.employmentId && !!this.salaryCodeId && this.isCalcValid &&
        this.resultUnits != null && this.resultUnits > 0 &&
        this.yearNormHours != null &&
        !!this.periodStart && !!this.periodEnd
    }
  },
  methods: {
    parseNumber (value) {
      if (value === '' || value == null) return null
      const parsed = parseFloat(String(value).replace(',', '.'))
      return isNaN(parsed) ? null : parsed
    },
    formatDecimal (value) {
      const num = parseFloat(value)
      return isNaN(num) ? '—' : num.toFixed(2).replace('.', ',')
    },
    formatPercent (value) {
      const num = parseFloat(value)
      return isNaN(num) ? '—' : num.toFixed(2).replace('.', ',') + ' %'
    },
    // Danner et supplement (kladde) i listen via samme API som "Hent UV-tillæg" – det sendes ikke til lønsystemet.
    generate () {
      if (!this.isValid || this.isSaving) return
      const units = parseFloat(this.resultUnits.toFixed(4))
      const installments = this.installments
      const unitsPerInstallment = installments > 0 ? parseFloat((units / installments).toFixed(4)) : units
      const startDate = moment(this.periodStart).format('YYYY-MM-DD')
      const endDate = moment(this.periodEnd).format('YYYY-MM-DD')
      this.isSaving = true
      docendo.createSalarySupplement(
        this.schoolId,
        this.schoolYearId,
        this.userId,
        this.employmentId,
        this.yearNormHours,
        startDate,
        endDate,
        this.salaryCode ? this.salaryCode.code : '',
        this.salaryCodeId,
        null,
        units,
        installments,
        unitsPerInstallment,
        success => {
          this.isSaving = false
          if (success && success.status === 'error') {
            eventbus.toast = {text: success.message || 'Kunne ikke danne UV-tillæg', success: false, position: 'bottomLeft'}
            return
          }
          eventbus.toast = {text: 'UV-tillæg dannet i listen', success: true, position: 'bottomLeft'}
          this.$emit('created', success)
        },
        failure => {
          console.log(failure)
          this.isSaving = false
          eventbus.toast = {text: failure && failure.message ? failure.message : 'Kunne ikke danne UV-tillæg', success: false, position: 'bottomLeft'}
        }
      )
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.d-modal {
  width: 720px;
  border-radius: 16px;
  position: relative;
}

.d-modal__header {
  margin-bottom: 0;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.tuv__subtitle {
  margin: 0.25rem 0 0;
  font-size: 13px;
  color: #6b7280;
}

.d-modal__content {
  padding: 1.5rem;
  max-height: calc(100vh - 52px - 70px);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.tuv__intro {
  margin: 0 0 1.5rem;
  line-height: 1.5;
}

.tuv__facts {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.tuv__fact {
  flex: 1;
  min-width: 120px;
  background: #f6f9fc;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.tuv__fact-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #6b7280;
}

.tuv__fact-value {
  font-size: 15px;
  font-weight: 600;
}

.do-form-row {
  display: flex;
  gap: 1rem;
}

.do-form-row > div {
  flex: 1;
}

.d-input-group {
  margin-bottom: 1.5rem;
}

.d-input-group .d-input {
  height: 34px;
}

.tuv__hint {
  margin: 0.4rem 0 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

.tuv__result {
  background: #f6f9fc;
  border-radius: 8px;
  padding: 1rem;
}

.tuv__result-steps {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 13px;
  color: #4b5563;
}

.tuv__result-row {
  display: flex;
  justify-content: space-between;
}

.tuv__result-row--sub {
  margin-top: 0.5rem;
  font-size: 13px;
  color: #4b5563;
}

.tuv__result-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e3e6ea;
  font-size: 15px;
}

.tuv__result-note {
  margin: 0.6rem 0 0;
  font-size: 12px;
  color: #a26206;
}

.d-modal__buttons {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #eee;
  margin: 0;
}

.d-modal__buttons > div {
  display: flex;
  align-items: center;
  gap: 1rem;
}
</style>



// WEBPACK FOOTER //
// UvLimitTidsbegraensetModal.vue?a1bed8f8